import { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Loader2, BookOpen } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import HelpSearchBar from "@/components/help/HelpSearchBar";
import HelpCategoryNav from "@/components/help/HelpCategoryNav";
import HelpArticleCard from "@/components/help/HelpArticleCard";
import RelatedArticles from "@/components/help/RelatedArticles";
import PopularArticlesWidget from "@/components/help/PopularArticlesWidget";
import HelpBreadcrumb from "@/components/help/HelpBreadcrumb";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Menu } from "lucide-react";
import { HelpArticle } from "@/types/help";

const HelpCenterPage = () => {
  const location = useLocation();
  const [articles, setArticles] = useState<HelpArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [expandedArticleId, setExpandedArticleId] = useState<string | null>(null);

  useEffect(() => {
    fetchArticles();
  }, []);

  useEffect(() => {
    if (location.state) {
      const state = location.state as any;
      if (state.selectedCategory) setSelectedCategory(state.selectedCategory);
      if (state.expandedArticleId) setExpandedArticleId(state.expandedArticleId);
      if (state.searchQuery) setSearchQuery(state.searchQuery);
    }
  }, [location.state]);

  const fetchArticles = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('help_articles' as any)
      .select('*')
      .eq('user_type', 'seller')
      .order('order_index', { ascending: true });

    if (error) {
      console.error('Error fetching articles:', error);
    } else {
      setArticles((data || []) as unknown as HelpArticle[]);
    }
    setLoading(false);
  };

  const categories = useMemo(() => {
    const categoryMap = new Map<string, number>();
    articles.forEach((article) => {
      categoryMap.set(article.category, (categoryMap.get(article.category) || 0) + 1);
    });
    return Array.from(categoryMap.entries()).map(([name, count]) => ({ name, count }));
  }, [articles]);

  const filteredArticles = useMemo(() => {
    let filtered = articles;

    if (selectedCategory) {
      filtered = filtered.filter((article) => article.category === selectedCategory);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((article) =>
        article.question.toLowerCase().includes(query) ||
        article.answer.toLowerCase().includes(query) ||
        article.search_keywords.some((keyword: string) => keyword.toLowerCase().includes(query))
      );
    }

    return filtered;
  }, [articles, selectedCategory, searchQuery]);

  const relatedArticles = useMemo(() => {
    if (!expandedArticleId) return [];
    const currentArticle = articles.find((a) => a.id === expandedArticleId);
    if (!currentArticle) return [];

    return articles
      .filter((a) => a.id !== expandedArticleId && a.category === currentArticle.category)
      .slice(0, 3);
  }, [expandedArticleId, articles]);

  const handleArticleClick = (articleId: string) => {
    setExpandedArticleId(articleId);
    document.getElementById(articleId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="py-8">
        <HelpBreadcrumb 
          userType="seller"
          category={selectedCategory}
        />

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <BookOpen className="h-8 w-8 text-primary" />
            <h1 className="text-3xl font-bold text-foreground">Seller Help Center</h1>
          </div>
          <p className="text-muted-foreground">
            Find answers to common questions and learn how to make the most of GoDyspo
          </p>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <HelpSearchBar value={searchQuery} onChange={setSearchQuery} />
        </div>

        {/* Main Content */}
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Mobile Category Menu */}
          <div className="lg:hidden">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" className="w-full">
                  <Menu className="h-4 w-4 mr-2" />
                  {selectedCategory || "All Categories"}
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="bg-background">
                <HelpCategoryNav
                  categories={categories}
                  selectedCategory={selectedCategory}
                  onSelectCategory={setSelectedCategory}
                />
              </SheetContent>
            </Sheet>
          </div>

          {/* Desktop Sidebar */}
          <aside className="hidden lg:block w-64 shrink-0">
            <div className="sticky top-4 space-y-6">
              <HelpCategoryNav
                categories={categories}
                selectedCategory={selectedCategory}
                onSelectCategory={setSelectedCategory}
              />
              <PopularArticlesWidget
                articles={articles}
                onArticleClick={handleArticleClick}
              />
            </div>
          </aside>

          {/* Articles */}
          <main className="flex-1 space-y-4">
            {filteredArticles.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">
                  {searchQuery
                    ? "No articles found matching your search."
                    : "No articles available in this category."}
                </p>
              </div>
            ) : (
              <>
                {filteredArticles.map((article) => (
                  <HelpArticleCard
                    key={article.id}
                    article={article}
                    defaultExpanded={article.id === expandedArticleId}
                  />
                ))}
                {expandedArticleId && relatedArticles.length > 0 && (
                  <RelatedArticles
                    articles={relatedArticles}
                    onArticleClick={handleArticleClick}
                  />
                )}
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default HelpCenterPage;
